<script setup lang="ts">
import { cn } from '@laam/lib/utils';
import { useVModel } from '@vueuse/core';
import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'vue';

const textAreaVariants = cva(
	/*tw*/ 'flex w-full items-center rounded-medium shadow-xs text-md border focus-within:ring-4 focus-within:ring-offset-0 focus-within:ring-opacity-15 p-xl focus-visible:ring-4 focus-visible:outline-none grow placeholder-gray-500 ',
	{
		variants: {
			size: {
				md: /*tw*/ 'px-2xl py-lg gap-md',
				sm: /*tw*/ 'px-xl py-md gap-md',
			},
			disabled: {
				true: 'bg-gray-50 text-gray-500',
			},
			hasError: {
				true: 'border-error-300 focus-within:ring-error-500 [&_svg]:fill-error-500',
				false:
					'border-gray-300 focus-within:ring-gray-400 [&_svg]:fill-gray-500',
			},
			defaultVariants: {
				size: 'md',
			},
		},
	},
);
export type TextAreaVariants = VariantProps<typeof textAreaVariants>;

const props = defineProps<{
	class?: HTMLAttributes['class'];
	defaultValue?: string | number;
	modelValue?: string | number;
	size?: TextAreaVariants['size'];
	rows?: string | number;
	cols?: string | number;
	hasError?: boolean;
	disabled?: boolean;
	limit?: string | number;
}>();

const emits = defineEmits<{
	(e: 'update:modelValue', payload: string | number): void;
}>();

const modelValue = useVModel(props, 'modelValue', emits, {
	passive: true,
	defaultValue: props.defaultValue,
});
</script>

<template>
	<textarea
		v-model="modelValue"
		:class="cn(textAreaVariants({ size, hasError, disabled }), props.class)"
		:rows="props.rows"
		:cols="props.cols"
		:maxlength="props.limit"
	/>
</template>
